import { useState } from 'react';
import { BaseModal, EmptyState, FormActions, FormField, FormSelect } from '@practium/ui';
import type { ManagementPlanDTO } from '@practium/features/api';
import styles from './AddLandingPlanModal.module.css';

interface AddLandingPlanModalProps {
	plans: ManagementPlanDTO[];
	isLoading: boolean;
	isAdding: boolean;
	hasReachedLimit: boolean;
	onAdd: (planId: string) => Promise<void>;
	onClose: () => void;
}

export const AddLandingPlanModal = ({
	plans,
	isLoading,
	isAdding,
	hasReachedLimit,
	onAdd,
	onClose,
}: AddLandingPlanModalProps) => {
	const [planId, setPlanId] = useState('');
	const options = plans.map(plan => {
		const hierarchy = typeof plan.hierarchy === 'object' ? plan.hierarchy.name : plan.hierarchy;
		const price = (plan.price / 100).toLocaleString('pt-BR', {
			style: 'currency',
			currency: 'BRL',
		});
		return { value: plan._id, label: `${plan.name} — ${hierarchy} — ${price}` };
	});

	const handleClose = () => {
		if (!isAdding) onClose();
	};

	return (
		<BaseModal
			isOpen
			title="Adicionar plano à landing page"
			subtitle="Selecione um plano ativo para exibi-lo na landing page."
			onClose={handleClose}
			isLoading={isAdding}
			maxWidth="42rem"
			actions={
				<FormActions
					onCancel={handleClose}
					onSave={() => {
						if (planId) void onAdd(planId);
					}}
					saveText="Adicionar plano"
					saveDisabled={!planId || isLoading}
					isLoading={isAdding}
				/>
			}
		>
			<div className={styles.content}>
				{hasReachedLimit ? (
					<EmptyState
						title="Limite de planos atingido"
						description="A landing page pode exibir até 3 planos. Remova um vínculo para adicionar outro."
					/>
				) : plans.length === 0 && !isLoading ? (
					<EmptyState
						title="Nenhum plano disponível"
						description="Não há outros planos ativos disponíveis para vincular."
					/>
				) : (
					<FormField label="Plano" required>
						<FormSelect
							value={planId}
							options={options}
							onChange={setPlanId}
							placeholder={isLoading ? 'Carregando planos...' : 'Selecione um plano'}
							searchable
							disabled={isLoading || isAdding}
						/>
					</FormField>
				)}
			</div>
		</BaseModal>
	);
};
